import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  GetUseOfForceReportForServiceProviderUnitResponse,
  UseOfForceReportHeaderModel,
  UseOfForceReportIncidentModel,
  UseOfForceUnitOverviewItemModel,
} from '../../api-service/clients';
import { createUseOfForceReport, deleteUseOfForceReportDraft, getUseOfForceReport, registerUseOfForceReportSending, updateUseOfForceReport } from './use-of-force-report-api';
import { UseOfForceReportDialog } from './use-of-force-report-dialog';
import { UseOfForceReportSendingDialog } from './use-of-force-report-sending-dialog';
import { isFromEndedMonth } from './use-of-force-report-months';
import { incidentCount, openReportPdf, supervisorText, UseOfForceSentReports } from './use-of-force-sent-reports';
import { UseOfForceReportActionPlans } from './use-of-force-report-action-plans';

type OpenDialog = 'create' | 'edit' | 'send';

interface UseOfForceReportPanelProps {
  serviceProviderUnitId: string;
  awaitingReport: UseOfForceUnitOverviewItemModel[];
  reloadCount: number;
  onChanged: () => void;
  onOpenIncident: (incident: UseOfForceReportIncidentModel) => void;
  children: React.ReactNode;
}

export function UseOfForceReportPanel(props: UseOfForceReportPanelProps) {
  const { serviceProviderUnitId, awaitingReport, reloadCount, onChanged, onOpenIncident, children } = props;
  const [report, setReport] = useState<GetUseOfForceReportForServiceProviderUnitResponse>();
  const [openDialog, setOpenDialog] = useState<OpenDialog>();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let isCurrent = true;
    async function load() {
      const response = await getUseOfForceReport(serviceProviderUnitId);
      if (isCurrent) setReport(response);
    }
    load();
    return () => {
      isCurrent = false;
    };
  }, [serviceProviderUnitId, reloadCount]);

  if (!report) return <>{children}</>;
  const draft = report.draft;

  async function saveAndReload(action: () => Promise<unknown>) {
    await action();
    setOpenDialog(undefined);
    onChanged();
  }

  async function deleteDraft() {
    if (!draft) return;
    await saveAndReload(() => deleteUseOfForceReportDraft(serviceProviderUnitId, draft.id));
  }

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {draft ? (
          <>
            <Typography variant="subtitle1">Indberetning til tilsynet (kladde)</Typography>
            <Typography variant="body2">
              {supervisorText(draft)} · {incidentCount(draft.incidentIds.length)}
              {draft.hygieneRestraintLogCount > 0 ? ` · ${draft.hygieneRestraintLogCount} bilag til § 136 c` : ''}
              {draft.header.operatorName ? ` · Driftsherre: ${draft.header.operatorName}` : ''}
            </Typography>
            {draft.missingForSending.length > 0 && (
              <Typography variant="body2" color="error">
                Mangler: {draft.missingForSending.join(', ')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button variant="contained" onClick={() => setOpenDialog('send')} disabled={draft.missingForSending.length > 0}>
                Registrér afsendelse
              </Button>
              <Button variant="outlined" onClick={() => openReportPdf(serviceProviderUnitId, draft.id)}>
                Hent PDF
              </Button>
              <Button variant="outlined" onClick={() => setOpenDialog('edit')}>
                Redigér
              </Button>
              <Button variant="outlined" onClick={deleteDraft}>
                Slet kladde
              </Button>
            </Box>
            <UseOfForceReportActionPlans serviceProviderUnitId={serviceProviderUnitId} report={draft} onChanged={onChanged} />
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">Saml de godkendte skemaer i en indberetning til tilsynet og driftsherren.</Typography>
            <Button variant="contained" onClick={() => setOpenDialog('create')} disabled={awaitingReport.length === 0}>
              Dan indberetning
            </Button>
          </Box>
        )}
        {openDialog === 'create' && (
          <UseOfForceReportDialog
            title="Dan indberetning til tilsynet"
            header={report.latestHeader ?? emptyHeader}
            items={awaitingReport}
            selectedIncidentIds={awaitingReport.filter(item => isFromEndedMonth(item, new Date())).map(item => item.incident.id)}
            onSave={(header, incidentIds) => saveAndReload(() => createUseOfForceReport(serviceProviderUnitId, header, incidentIds))}
            onClose={() => setOpenDialog(undefined)}
          />
        )}
        {openDialog === 'edit' && draft && (
          <UseOfForceReportDialog
            title="Redigér indberetning til tilsynet"
            header={draft.header}
            items={awaitingReport}
            selectedIncidentIds={draft.incidentIds}
            onSave={(header, incidentIds) => saveAndReload(() => updateUseOfForceReport(serviceProviderUnitId, draft.id, header, incidentIds))}
            onClose={() => setOpenDialog(undefined)}
          />
        )}
        {openDialog === 'send' && draft && (
          <UseOfForceReportSendingDialog
            report={draft}
            onSave={(sentToSupervisionAt, operatorInformedAt) =>
              saveAndReload(async () => {
                const sent = await registerUseOfForceReportSending(serviceProviderUnitId, draft.id, sentToSupervisionAt, operatorInformedAt);
                enqueueSnackbar(`Afsendelsen er registreret. ${incidentCount(sent.incidentIds.length)} er markeret som indberettet.`, { variant: 'success' });
              })
            }
            onClose={() => setOpenDialog(undefined)}
          />
        )}
      </Paper>
      {children}
      <UseOfForceSentReports serviceProviderUnitId={serviceProviderUnitId} reports={report.sent} onOpenIncident={onOpenIncident} />
    </>
  );
}

const emptyHeader: UseOfForceReportHeaderModel = {};
